/**
 * Capture framing (W42 owner report, 2026-10-14): a capture shot must never
 * show the edge of the map.
 *
 * The 2026-W42 devlog's "Music in the works" segment (`driftwell-market-scout`,
 * preset `wide`, slow orbit) opened on flat grass ending in white void at the
 * right of frame, and only lost the edge as the orbit swung round. Nothing chose
 * the starting heading: the preset yaw is a fixed angle (π, "behind the spawn
 * facing +Z") applied wherever the seeded character happens to stand, and the
 * slow orbit then turned through a full revolution — through every heading that
 * faces the void.
 *
 * The rule, computed against the ZONE BOUNDS the ground is built on
 * (`boundsGround` in scene/plan.ts covers exactly `ack.bounds`):
 *
 *   1. For a pose (target, yaw, pitch, dist) cast a grid of rays through the
 *      camera frustum onto the ground plane. A ray that lands outside the zone
 *      rectangle (inset by {@link FRAMING_EDGE_MARGIN}) is ground the camera
 *      would draw as void. Rays that miss the ground (sky) do not count, nor do
 *      rays landing so deep in the fog that ground and void are the same colour
 *      (fog visibility below {@link VOID_MIN_VISIBILITY}). The share of void
 *      rays is the pose's VOID EXPOSURE.
 *   2. Sample every heading around the target ({@link FRAMING_YAW_SAMPLES}). The
 *      headings with zero exposure form the SAFE ARCS.
 *   3. The orbit STARTS on a safe heading — the preset's own yaw when it is
 *      already safe (composition preserved), otherwise the centre of the widest
 *      safe arc (the heading facing furthest into the map interior) — and the
 *      drift is CONSTRAINED to that arc: it sweeps to the arc's end and reverses
 *      (a slow pan back and forth) instead of turning round into the void.
 *   4. When every heading is safe the orbit is free (full revolutions, as
 *      before). When none is, the least-exposed heading is held still and its
 *      exposure reported, so the capture tool can refuse the shot
 *      (`captureFraming.voidShare` in the dev scene dump).
 *
 * A framing describes ONE target position. The W42 re-render proved it must not
 * outlive it: the `verdant-reach-northwatch` delver was killed mid-hold by a
 * wandering creature and respawned at the zone spawn (-72, 0) — 8 m from the west
 * edge — while the camera kept sweeping the arc resolved for the checkpoint
 * (-44, 38), and the last ~5 s of the music segment framed the void. So the
 * framing is re-resolved whenever it goes STALE ({@link framingStale}): the target
 * has moved more than {@link FRAMING_REFRAME_DRIFT} from where it was resolved, or
 * the current pose shows real void anyway.
 *
 * Presentation-only and client-local, like the rest of capture mode: yaw is a
 * bare angle with no bound (camera.ts), pitch and distance are never touched, so
 * the CAMERA_BOUNDS zoom envelope cannot widen. Pure — no DOM, no THREE — so the
 * Node harness tests it directly.
 */

import { orbitPose } from './camera.js';

/** The zone rectangle the ground covers (the `ZoneBounds` shape). */
export interface FramingRect {
  readonly minX: number;
  readonly minZ: number;
  readonly maxX: number;
  readonly maxZ: number;
}

/** The camera's projection and the scene fog that bounds how far ground reads. */
export interface FramingView {
  readonly fovYDeg: number;
  readonly aspect: number;
  /** Linear fog range (camera distance, m): ground fades into the fog colour across it. */
  readonly fogNear: number;
  readonly fogFar: number;
}

/** A capture camera pose (the preset fields that matter for framing). */
export interface FramingPose {
  readonly yaw: number;
  readonly pitch: number;
  readonly dist: number;
}

/** Ground point the camera follows (the own avatar). `y` is the ground height there. */
export interface FramingTarget {
  readonly x: number;
  readonly y?: number;
  readonly z: number;
}

/**
 * How far inside the zone edge the visible ground must stay (metres). The edge is
 * a hard line between ground and void; a frame whose corner just touches it reads
 * as "the map ends here", so the rule keeps a strip of real ground in hand.
 */
export const FRAMING_EDGE_MARGIN = 1;

/**
 * A ray past the zone edge counts as void only while the fog still lets ground
 * read as ground (1 = unfogged, 0 = fully fog colour). Below this the would-be
 * ground is already the fog/horizon colour the void is drawn in.
 */
export const VOID_MIN_VISIBILITY = 0.25;

/** Headings sampled around the target (2° apart). */
export const FRAMING_YAW_SAMPLES = 180;

/** Ray grid across the frame (16:9-ish, edges and corners included). */
const RAYS_X = 33;
const RAYS_Y = 19;

/**
 * The share of the frame (0..1) that would show void past the zone edge from
 * this pose — 0 when every visible patch of ground is inside the zone.
 */
export function voidExposure(
  target: FramingTarget,
  pose: FramingPose,
  rect: FramingRect,
  view: FramingView,
  margin = FRAMING_EDGE_MARGIN,
): number {
  const groundY = target.y ?? 0;
  const cam = orbitPose({ x: target.x, y: groundY, z: target.z }, pose.yaw, pose.pitch, pose.dist);
  // Camera basis: forward toward the look point, right = forward × up, up' = right × forward.
  let fx = cam.lookX - cam.x;
  let fy = cam.lookY - cam.y;
  let fz = cam.lookZ - cam.z;
  const fl = Math.hypot(fx, fy, fz) || 1;
  fx /= fl;
  fy /= fl;
  fz /= fl;
  let rx = -fz;
  let rz = fx;
  const rl = Math.hypot(rx, rz) || 1;
  rx /= rl;
  rz /= rl;
  const ux = -rz * fy;
  const uy = rz * fx - rx * fz;
  const uz = rx * fy;

  const ty = Math.tan((view.fovYDeg * Math.PI) / 360);
  const tx = ty * view.aspect;
  const minX = rect.minX + margin;
  const maxX = rect.maxX - margin;
  const minZ = rect.minZ + margin;
  const maxZ = rect.maxZ - margin;
  const fogSpan = Math.max(1e-6, view.fogFar - view.fogNear);
  // Beyond this camera distance the fog has swallowed the ground (visibility < the floor).
  const visibleTo = view.fogNear + (1 - VOID_MIN_VISIBILITY) * fogSpan;

  let voidRays = 0;
  for (let i = 0; i < RAYS_X; i += 1) {
    const u = -1 + (2 * i) / (RAYS_X - 1);
    for (let j = 0; j < RAYS_Y; j += 1) {
      const v = -1 + (2 * j) / (RAYS_Y - 1);
      const dx = fx + u * tx * rx + v * ty * ux;
      const dy = fy + v * ty * uy;
      const dz = fz + u * tx * rz + v * ty * uz;
      if (dy >= -1e-6) continue; // at or above the horizon: sky, never ground
      const t = (groundY - cam.y) / dy;
      if (t * Math.hypot(dx, dy, dz) > visibleTo) continue; // lost in the fog
      const px = cam.x + t * dx;
      const pz = cam.z + t * dz;
      if (px < minX || px > maxX || pz < minZ || pz > maxZ) voidRays += 1;
    }
  }
  return voidRays / (RAYS_X * RAYS_Y);
}

/**
 * The resolved framing for an orbit:
 * - `free`  — no heading shows the void; the orbit turns freely from `startYaw`.
 * - `arc`   — the orbit starts at `startYaw` and sweeps only within [minYaw, maxYaw].
 * - `hold`  — every heading shows some void; `startYaw` is the least-exposed one,
 *             held still, and `exposure` says how much (the capture should refuse it).
 * Yaws are UNWRAPPED around the reference yaw the framing was resolved from, so
 * the orbit steps through them without a ±π seam.
 */
export type OrbitFraming =
  | { readonly kind: 'free'; readonly startYaw: number }
  | {
      readonly kind: 'arc';
      readonly startYaw: number;
      readonly minYaw: number;
      readonly maxYaw: number;
    }
  | { readonly kind: 'hold'; readonly startYaw: number; readonly exposure: number };

/**
 * Resolve where an orbit around `target` may look (see the module comment).
 * `pose.yaw` is the reference: the preset's / the camera's current heading.
 */
export function resolveOrbitFraming(
  target: FramingTarget,
  pose: FramingPose,
  rect: FramingRect,
  view: FramingView,
  samples = FRAMING_YAW_SAMPLES,
  margin = FRAMING_EDGE_MARGIN,
): OrbitFraming {
  const n = Math.max(8, Math.floor(samples));
  const step = (2 * Math.PI) / n;
  const yawAt = (i: number): number => pose.yaw + i * step;
  const exposure: number[] = [];
  for (let i = 0; i < n; i += 1) {
    exposure.push(voidExposure(target, { ...pose, yaw: yawAt(i) }, rect, view, margin));
  }
  const safe = exposure.map((e) => e === 0);
  if (safe.every(Boolean)) return { kind: 'free', startYaw: pose.yaw };
  if (!safe.some(Boolean)) {
    let best = 0;
    for (let i = 1; i < n; i += 1) {
      if ((exposure[i] ?? Infinity) < (exposure[best] ?? Infinity)) best = i;
    }
    return { kind: 'hold', startYaw: yawAt(best), exposure: exposure[best] ?? 0 };
  }

  // Contiguous safe runs on the circle, scanned from just after an unsafe sample so
  // no run is split by the array seam. Indices are unwrapped in (firstUnsafe, firstUnsafe + n].
  const firstUnsafe = safe.findIndex((s) => !s);
  const runs: { lo: number; hi: number }[] = [];
  let open: { lo: number; hi: number } | null = null;
  for (let k = 1; k <= n; k += 1) {
    const i = firstUnsafe + k;
    if (safe[i % n]) {
      if (open === null) open = { lo: i, hi: i };
      else open.hi = i;
    } else if (open !== null) {
      runs.push(open);
      open = null;
    }
  }
  if (open !== null) runs.push(open);

  // Re-express each run in the frame where index 0 is the reference yaw: shift it
  // down a turn when it lies past index 0's first lap (so a run holding index n
  // ≡ 0 straddles 0).
  const framed = runs.map((r) => (r.hi >= n ? { lo: r.lo - n, hi: r.hi - n } : r));
  const containing = framed.find((r) => r.lo <= 0 && r.hi >= 0);
  if (containing !== undefined) {
    return {
      kind: 'arc',
      startYaw: pose.yaw,
      minYaw: yawAt(containing.lo),
      maxYaw: yawAt(containing.hi),
    };
  }
  // The reference heading shows the void: start at the centre of the widest safe arc
  // — the heading facing furthest into the map — and sweep within it.
  let widest = framed[0] ?? { lo: 0, hi: 0 };
  for (const r of framed) if (r.hi - r.lo > widest.hi - widest.lo) widest = r;
  let { lo, hi } = widest;
  // Take the short way round from the reference heading.
  if ((lo + hi) / 2 > n / 2) {
    lo -= n;
    hi -= n;
  }
  return { kind: 'arc', startYaw: yawAt((lo + hi) / 2), minYaw: yawAt(lo), maxYaw: yawAt(hi) };
}

/**
 * How far (metres, on the ground plane) the target may move from where its
 * framing was resolved before the framing is re-resolved. Small enough that a
 * respawn, a knock-back or a server correction re-frames at once; large enough
 * that the idle sway of a held avatar never does.
 */
export const FRAMING_REFRAME_DRIFT = 1;

/**
 * Whether a resolved framing no longer describes the shot (W42 re-render): the
 * target has moved more than {@link FRAMING_REFRAME_DRIFT} from `anchor` (where
 * the framing was resolved), or — for a framing that promised a clean shot — the
 * current pose shows void past the REAL zone edge (margin 0, so the strip of
 * ground the arc keeps in hand never triggers a re-frame on its own). A `hold`
 * framing already reports its exposure, so only movement re-resolves it.
 */
export function framingStale(
  anchor: FramingTarget,
  target: FramingTarget,
  pose: FramingPose,
  framing: OrbitFraming,
  rect: FramingRect,
  view: FramingView,
): boolean {
  if (Math.hypot(target.x - anchor.x, target.z - anchor.z) > FRAMING_REFRAME_DRIFT) return true;
  if (framing.kind === 'hold') return false;
  return voidExposure(target, pose, rect, view, 0) > 0;
}

/**
 * Advance a framed orbit by `delta` radians. Free orbits turn; held ones stay;
 * arcs sweep and reverse at their ends (`dir` flips), never leaving the arc.
 */
export function stepFramedOrbit(
  yaw: number,
  delta: number,
  framing: OrbitFraming,
  dir: 1 | -1,
): { yaw: number; dir: 1 | -1 } {
  if (framing.kind === 'free') return { yaw: yaw + delta, dir };
  if (framing.kind === 'hold') return { yaw: framing.startYaw, dir };
  const { minYaw, maxYaw } = framing;
  if (maxYaw <= minYaw) return { yaw: minYaw, dir };
  let next = yaw + dir * delta;
  let nextDir = dir;
  if (next > maxYaw) {
    next = maxYaw - (next - maxYaw);
    nextDir = -1;
  } else if (next < minYaw) {
    next = minYaw + (minYaw - next);
    nextDir = 1;
  }
  return { yaw: Math.min(maxYaw, Math.max(minYaw, next)), dir: nextDir };
}
